import React, { useEffect, useState, useRef } from "react";
import RichTextEditor from "@/components/HtmlEditor/RichTextEditor";
import styles from "../../Challenges.module.scss";
import toast from "react-hot-toast";
import { fetchAllTagsAPI } from "@/services/api/Question";
import { fetchLanguagesAPI } from "@/services/api/ProgrammingLanguage";
import ReactSelect from "react-select";
import { formatArrayInput, stripBrackets, getTemplateForLanguage } from "@/lib/utils";
import Editor from "@monaco-editor/react";

// ─── Types ────────────────────────────────────────────────────────────────────
type DataType = "String" | "Integer" | "Array of strings" | "Array of integers";

interface CustomTag {
    id: number;
    name: string;
}

interface TestCase {
    input: string;
    output: string;
    isHidden: boolean;
}

interface IOModalProps {
    onClose: () => void;
    onUpload: (data: any) => void;
    editItem?: any | null;
}

const getMonacoLanguage = (languageName = "") => {
    const baseLang = languageName.toLowerCase();
    if (baseLang.includes("python")) return "python";
    if (baseLang.includes("javascript") || baseLang.includes("js")) return "javascript";
    if (baseLang.includes("typescript") || baseLang.includes("ts")) return "typescript";
    if (baseLang.includes("java")) return "java";
    if (baseLang.includes("c++") || baseLang.includes("cpp")) return "cpp";
    if (baseLang.includes("c#") || baseLang.includes("csharp")) return "csharp";
    if (baseLang.includes("c")) return "c";
    return "plaintext";
};

// ─── IOModal ──────────────────────────────────────────────────────────────────
const IOModal = ({ onClose, onUpload, editItem }: IOModalProps) => {
    const isEditMode = !!editItem;
    const modalBodyRef = useRef<HTMLDivElement>(null);
    const shouldScrollRef = useRef(false);

    // Payload Fields
    const [id, setId] = useState<number | null>(null);
    const [name, setName] = useState("");
    const [difficulty, setDifficulty] = useState("Easy");
    const [shortDesc, setShortDesc] = useState("");
    const [description, setDescription] = useState("");
    const [inputType, setInputType] = useState<DataType>("String");
    const [outputType, setOutputType] = useState<DataType>("String");
    const [timeLimit, setTimeLimit] = useState<number | string>(15);
    const [memoryLimit, setMemoryLimit] = useState<number | string>(256);
    const [selectedTags, setSelectedTags] = useState<number[]>([]);

    // Languages State
    const [languages, setLanguages] = useState<any[]>([]);
    const [selectedLanguages, setSelectedLanguages] = useState<any[]>([]);
    const [activeLanguageIndex, setActiveLanguageIndex] = useState<number>(-1);
    const [useCustomTemplate, setUseCustomTemplate] = useState(false);

    // UI State
    const [testCases, setTestCases] = useState<TestCase[]>([]);
    const [showTestCases, setShowTestCases] = useState(false);
    const [allTags, setAllTags] = useState<CustomTag[]>([]);
    const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);

    const dataTypes: DataType[] = ["String", "Integer", "Array of strings", "Array of integers"];

    const [tagPage, setTagPage] = useState(0);
    const [hasMoreTags, setHasMoreTags] = useState(true);
    const [isFetchingTags, setIsFetchingTags] = useState(false);
    const [tagSearch, setTagSearch] = useState("");

    useEffect(() => {
        const handler = setTimeout(() => {
            setTagPage(0);
            getTags(0, true, tagSearch);
        }, 500);
        return () => clearTimeout(handler);
    }, [tagSearch]);

    useEffect(() => {
        const loadLanguages = async () => {
            try {
                const res = await fetchLanguagesAPI(0, 100, "");
                let langs: any[] = [];
                if (res) {
                    if (Array.isArray(res)) {
                        langs = res;
                    } else if (res.data && res.data.results && Array.isArray(res.data.results)) {
                        langs = res.data.results;
                    }
                }
                const allowedIds = [75, 76, 48, 52, 49, 53, 50, 54, 62, 63, 70, 71, 74];
                const filteredLangs = langs.filter((lang: any) => allowedIds.includes(lang.judge0Id));
                setLanguages(filteredLangs);
            } catch (err) {
                console.error("Failed to load languages in IOModal", err);
            }
        };
        loadLanguages();
    }, []);

    const getTags = async (pageNo: number, reset: boolean = false, search: string = "") => {
        if (isFetchingTags || (!hasMoreTags && !reset)) return;

        setIsFetchingTags(true);
        try {
            const res = await fetchAllTagsAPI(pageNo, 50, "", search);
            const tagsList = res?.data?.results || res?.data || [];

            if (reset) {
                setAllTags(Array.isArray(tagsList) ? tagsList : []);
            } else {
                setAllTags(prev => [...prev, ...tagsList]);
            }

            // If we got fewer than 10 results, assume no more pages
            if (tagsList.length < 10) {
                setHasMoreTags(false);
            } else {
                setHasMoreTags(true);
            }
        } catch (err) {
            console.error("Tags load failed", err);
        } finally {
            setIsFetchingTags(false);
        }
    };

    const handleTagsScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        if (target.scrollHeight - target.scrollTop <= target.clientHeight + 10) {
            if (hasMoreTags && !isFetchingTags) {
                const nextPage = tagPage + 1;
                setTagPage(nextPage);
                getTags(nextPage, false, tagSearch);
            }
        }
    };

    const handleTagSelect = (tagId: number) => {
        if (!tagId || selectedTags.length >= 5 || selectedTags.includes(tagId)) return;
        setSelectedTags(prev => [...prev, tagId]);
    };

    const removeTag = (tagId: number) => {
        setSelectedTags(prev => prev.filter(id => id !== tagId));
    };

    useEffect(() => {
        if (editItem) {
            const data = editItem?.data || editItem;
            setId(data.id || null);
            setName(data.title || "");
            setShortDesc(data.shortDescription || "");
            setDescription(data.description || "");
            setTimeLimit(data.timeLimit || 2);
            setMemoryLimit(data.memoryLimit || 256);

            if (data.tags && Array.isArray(data.tags)) {
                setSelectedTags(data.tags.map((t: any) => t.id));
            }

            if (data?.difficultyLevel) {
                const diff = String(data.difficultyLevel).toLowerCase();
                setDifficulty(diff.charAt(0).toUpperCase() + diff.slice(1));
            }

            if (data?.inputType) {
                const type = String(data.inputType).toLowerCase().replace(/_/g, " ");
                setInputType(type.charAt(0).toUpperCase() + type.slice(1) as DataType);
            }

            if (data?.outputType) {
                const type = String(data.outputType).toLowerCase().replace(/_/g, " ");
                setOutputType(type.charAt(0).toUpperCase() + type.slice(1) as DataType);
            }

            if (data.testCases && Array.isArray(data.testCases)) {
                setTestCases(
                    data.testCases.map((tc: any) => ({
                        input: tc.input || "",
                        // Check both in case backend fixing the typo later
                        output: tc.expectedOutput || tc.exceptedOutput || "",
                        isHidden: tc.isHidden !== undefined ? tc.isHidden : true
                    }))
                );
                setShowTestCases(true);
            }

            if (data.starterCodes && Array.isArray(data.starterCodes) && data.starterCodes.length > 0) {
                const loaded = data.starterCodes.map((sc: any) => ({
                    starterCodeId: sc.id,
                    name: sc.languageName,
                    judge0Id: sc.judge0Id,
                    codeTemplate: sc.codeTemplate || ""
                }));
                setSelectedLanguages(loaded);
                setActiveLanguageIndex(0);
            } else {
                setSelectedLanguages([]);
                setActiveLanguageIndex(-1);
                setUseCustomTemplate(false);
            }
        }
    }, [editItem]);

    useEffect(() => {
        if (shouldScrollRef.current && modalBodyRef.current) {
            shouldScrollRef.current = false;
            const container = modalBodyRef.current;
            setTimeout(() => {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: "smooth"
                });
            }, 50);
        }
    }, [testCases]);

    const addTestCase = () => {
        setTestCases(prev => [...prev, { input: "", output: "", isHidden: true }]);
        shouldScrollRef.current = true;
    };

    const updateTestCase = (i: number, field: keyof TestCase, val: string) => {
        setTestCases(prev =>
            prev.map((tc, idx) => (idx === i ? { ...tc, [field]: val } : tc))
        );
    };

    const removeTestCase = (i: number) => {
        setTestCases(prev => prev.filter((_, idx) => idx !== i));
    };

    const handleTagToggle = (tagId: number) => {
        setSelectedTags(prev =>
            prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
        );
    };

    const handleUpload = () => {
        if (!name.trim()) {
            toast.error("Challenge name is required");
            return;
        }

        const validTestCases = testCases.filter(tc => tc.input.trim() && tc.output.trim());
        if (!isEditMode && validTestCases.length === 0) {
            toast.error("At least one valid test case is compulsory!");
            return;
        }

        if (useCustomTemplate && selectedLanguages.length === 0) {
            toast.error("At least one programming language must be selected!");
            return;
        }

        const payload: any = {
            title: name,
            tagsId: selectedTags,
            shortDescription: shortDesc,
            description,
            difficultyLevel: String(difficulty || "Easy").toUpperCase(),
            timeLimit: timeLimit === "" ? 0 : Number(timeLimit),
            memoryLimit: memoryLimit === "" ? 0 : Number(memoryLimit),
            inputType: String(inputType || "String").toUpperCase().replace(/ /g, "_"),
            outputType: String(outputType || "String").toUpperCase().replace(/ /g, "_"),
            dbFileUrl: "",
            challengeType: "CODING",
            testCase: validTestCases.map((tc) => ({
                input: tc.input,
                expectedOutput: tc.output,
                isHidden: tc.isHidden,
            })),
            starterCodes: useCustomTemplate ? selectedLanguages.map((lang) => ({
                id: lang.starterCodeId || 0,
                judge0Id: lang.judge0Id,
                languageName: lang.name,
                codeTemplate: lang.codeTemplate || ""
            })) : []
        };

        if (isEditMode && id) {
            payload.id = id;
        }

        onUpload(payload);
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal} style={{ width: "800px" }}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalHeaderTitle}>
                        {isEditMode ? "Edit input/output challenge" : "New input/output custom challenge"}
                    </span>
                    <button onClick={onClose} className={styles.closeBtn}>✕</button>
                </div>

                <div ref={modalBodyRef} className={styles.modalBody}>
                    <label className={styles.label}>Name:</label>
                    <input
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Custom challenge name"
                        className={styles.input}
                    />

                    <div className={styles.row}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Difficulty:</label>
                            <select
                                value={difficulty}
                                onChange={e => setDifficulty(e.target.value)}
                                className={styles.select}
                            >
                                <option>Easy</option>
                                <option>Medium</option>
                                <option>Hard</option>
                            </select>
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Time Limit (min):</label>
                            <input
                                type="number"
                                value={timeLimit}
                                onChange={e => {
                                    const val = e.target.value.replace(/^0+(?!$)/, "");
                                    setTimeLimit(val);
                                }}
                                className={styles.input}
                            />
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Memory Limit (MB):</label>
                            <input
                                type="number"
                                value={memoryLimit}
                                onChange={e => {
                                    const val = e.target.value.replace(/^0+(?!$)/, "");
                                    setMemoryLimit(val);
                                }}
                                className={styles.input}
                            />
                        </div>
                    </div>

                    <div className={styles.fullRow} style={{ marginBottom: 16 }}>
                        <label className={styles.label}>Tags (max 5)</label>

                        <ReactSelect
                            inputValue={tagSearch}
                            onInputChange={(val, { action }) => {
                                if (action === 'input-change') setTagSearch(val);
                            }}
                            onChange={(selected: any) => {
                                if (selected) {
                                    handleTagSelect(selected.value);
                                    setTagSearch("");
                                }
                            }}
                            options={allTags
                                .filter(tag => !selectedTags.includes(tag.id))
                                .map((tag) => ({ label: tag.name, value: tag.id }))}
                            placeholder="Search and select tags..."
                            value={null}
                            menuPortalTarget={document.body}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    backgroundColor: 'var(--surface-2, #f8fafc)',
                                    borderColor: 'var(--border, #cbd5e1)',
                                    borderRadius: '8px',
                                    boxShadow: 'none',
                                    color: 'var(--text, #1e293b)',
                                    '&:hover': { borderColor: 'var(--accent, #3b82f6)' }
                                }),
                                input: (base) => ({
                                    ...base,
                                    color: 'var(--text, #1e293b)',
                                    margin: 0,
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                }),
                                singleValue: (base) => ({
                                    ...base,
                                    color: 'var(--text, #1e293b)',
                                }),
                                placeholder: (base) => ({
                                    ...base,
                                    color: 'var(--text-3, #94a3b8)',
                                }),
                                menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                }),
                                menu: (base) => ({
                                    ...base,
                                    backgroundColor: 'var(--surface, #ffffff)',
                                    border: '1px solid var(--border, #e2e8f0)',
                                    zIndex: 9999,
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isSelected
                                        ? 'var(--accent, #3b82f6)'
                                        : state.isFocused
                                            ? 'var(--surface-2, #f1f5f9)'
                                            : 'var(--surface, #ffffff)',
                                    color: state.isSelected
                                        ? '#ffffff'
                                        : 'var(--text, #1e293b)',
                                    cursor: 'pointer',
                                    '&:active': {
                                        backgroundColor: 'var(--accent, #3b82f6)'
                                    }
                                })
                            }}
                        />

                        <div className={styles.tags} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {selectedTags.map((id) => {
                                const tag = allTags.find(t => t.id === id);
                                if (!tag) return null;
                                return (
                                    <span key={tag.id} className={styles.tagBadge} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#e2e8f0', padding: '4px 8px', borderRadius: '4px' }}>
                                        {tag.name}
                                        <span style={{ cursor: 'pointer' }} onClick={() => removeTag(tag.id)}>✕</span>
                                    </span>
                                );
                            })}
                        </div>
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.label}>Short description:</label>
                        <input
                            value={shortDesc}
                            onChange={e => setShortDesc(e.target.value)}
                            placeholder="Optional short description..."
                            className={styles.input}
                        />
                    </div>

                    <div className={styles.fieldGroup} style={{ marginBottom: 16 }}>
                        <label className={styles.label}>Description:</label>
                        <RichTextEditor value={description} onChange={setDescription} />
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, color: 'var(--text, #1e293b)' }}>
                            <input
                                type="checkbox"
                                checked={useCustomTemplate}
                                onChange={(e) => {
                                    setUseCustomTemplate(e.target.checked);
                                    if (!e.target.checked) {
                                        setSelectedLanguages([]);
                                        setActiveLanguageIndex(-1);
                                    }
                                }}
                                style={{ width: '16px', height: '16px', accentColor: 'var(--accent, #14b8a6)', cursor: 'pointer' }}
                            />
                            Create custom template
                        </label>
                    </div>

                    {useCustomTemplate && (
                        <>
                            <div className={styles.fieldGroup} style={{ marginBottom: 16 }}>
                                <label className={styles.label}>Programming Language:</label>
                                <select
                                    value=""
                                    onChange={e => {
                                        const val = e.target.value;
                                        if (val) {
                                            const [judge0IdStr, name] = val.split(":");
                                            const judge0Id = Number(judge0IdStr);

                                            const existingIndex = selectedLanguages.findIndex(l => l.judge0Id === judge0Id);
                                            if (existingIndex !== -1) {
                                                setActiveLanguageIndex(existingIndex);
                                            } else {
                                                const defaultTemplate = getTemplateForLanguage(name);
                                                const newLang = {
                                                    starterCodeId: 0,
                                                    name,
                                                    judge0Id,
                                                    codeTemplate: defaultTemplate || ""
                                                };
                                                const updated = [...selectedLanguages, newLang];
                                                setSelectedLanguages(updated);
                                                setActiveLanguageIndex(updated.length - 1);
                                            }
                                        }
                                    }}
                                    className={styles.select}
                                >
                                    <option value="">Select a language...</option>
                                    {languages.map((lang) => (
                                        <option key={lang.id} value={`${lang.judge0Id}:${lang.name}`}>
                                            {lang.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedLanguages.length > 0 && (
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                                    {selectedLanguages.map((lang, idx) => {
                                        const isActive = idx === activeLanguageIndex;
                                        return (
                                            <span
                                                key={lang.judge0Id}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '6px 12px',
                                                    borderRadius: '20px',
                                                    fontSize: '13px',
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    background: isActive ? 'var(--accent, #3b82f6)' : 'var(--surface-2, #f1f5f9)',
                                                    color: isActive ? '#ffffff' : 'var(--text-2, #475569)',
                                                    border: '1px solid ' + (isActive ? 'var(--accent, #3b82f6)' : 'var(--border, #cbd5e1)'),
                                                    transition: 'all 0.2s'
                                                }}
                                                onClick={() => setActiveLanguageIndex(idx)}
                                            >
                                                {lang.name}
                                                <span
                                                    style={{
                                                        cursor: 'pointer',
                                                        fontWeight: 'bold',
                                                        opacity: 0.7
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const updated = selectedLanguages.filter((_, i) => i !== idx);
                                                        setSelectedLanguages(updated);
                                                        if (activeLanguageIndex === idx) {
                                                            setActiveLanguageIndex(updated.length > 0 ? 0 : -1);
                                                        } else if (activeLanguageIndex > idx) {
                                                            setActiveLanguageIndex(activeLanguageIndex - 1);
                                                        }
                                                    }}
                                                >
                                                    ✕
                                                </span>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {activeLanguageIndex !== -1 && selectedLanguages[activeLanguageIndex] && (
                                <div style={{ marginBottom: 16 }}>
                                    <label className={styles.label} style={{ fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                                        Starter Code Template ({selectedLanguages[activeLanguageIndex].name}) :
                                    </label>
                                    <div style={{ height: '250px', border: '1px solid var(--border, #e2e8f0)', borderRadius: '8px', overflow: 'hidden' }}>
                                        <Editor
                                            height="100%"
                                            language={getMonacoLanguage(selectedLanguages[activeLanguageIndex].name)}
                                            value={selectedLanguages[activeLanguageIndex].codeTemplate}
                                            onChange={(val) => {
                                                setSelectedLanguages(prev =>
                                                    prev.map((l, i) =>
                                                        i === activeLanguageIndex
                                                            ? { ...l, codeTemplate: val || "" }
                                                            : l
                                                    )
                                                );
                                            }}
                                            options={{
                                                fontSize: 13,
                                                minimap: { enabled: false },
                                                scrollBeyondLastLine: false,
                                                automaticLayout: true,
                                                tabSize: 4,
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <div className={styles.typeRow}>
                        <div className={styles.typeCol}>
                            <label className={styles.smallLabel}>Input type</label>
                            <select
                                value={inputType}
                                onChange={e => setInputType(e.target.value as DataType)}
                                className={styles.select}
                            >
                                {dataTypes.map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className={styles.typeCol}>
                            <label className={styles.smallLabel}>Output type</label>
                            <select
                                value={outputType}
                                onChange={e => setOutputType(e.target.value as DataType)}
                                className={styles.select}
                            >
                                {dataTypes.map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                    </div>

                    {!showTestCases ? (
                        <button
                            onClick={() => { setShowTestCases(true); addTestCase(); }}
                            className={styles.addTestBtn}
                        >
                            + Add Test Cases
                        </button>
                    ) : (
                        <div style={{ marginTop: 16 }}>
                            <div className={styles.testCasesHeader}>
                                <span className={styles.testCasesTitle}>Test Cases</span>
                                <button onClick={addTestCase} className={styles.addMoreBtn}>+ Add More</button>
                            </div>

                            {testCases.map((tc, i) => (
                                <div key={i} className={styles.testCaseRow}>
                                    <span className={styles.testCaseNum}>{i + 1}</span>
                                    <div className={styles.tcInputCol}>
                                        <textarea
                                            value={tc.input}
                                            onChange={e => updateTestCase(i, "input", e.target.value)}
                                            placeholder={`Input ${i + 1}`}
                                            className={styles.tcInput}
                                            rows={3}
                                            style={{ resize: "vertical", minHeight: "3px  !important ", fontFamily: "monospace", whiteSpace: "pre" }}
                                        />
                                    </div>
                                    <span className={styles.tcArrow}>→</span>
                                    <div className={styles.tcInputCol}>
                                        <textarea
                                            value={tc.output}
                                            onChange={e => updateTestCase(i, "output", e.target.value)}
                                            placeholder={`Output ${i + 1}`}
                                            className={styles.tcInput}
                                            rows={3}
                                            style={{ resize: "vertical", minHeight: "3px  !important ", fontFamily: "monospace", whiteSpace: "pre" }}
                                        />
                                    </div>
                                    <label className={styles.hiddenCheckbox} title="Hide test case">
                                        <input
                                            type="checkbox"
                                            checked={tc.isHidden}
                                            onChange={e => {
                                                setTestCases(prev =>
                                                    prev.map((item, idx) =>
                                                        idx === i ? { ...item, isHidden: e.target.checked } : item
                                                    )
                                                );
                                            }}
                                        />
                                        <span className={styles.hiddenLabel}>Hide</span>
                                    </label>
                                    <button onClick={() => removeTestCase(i)} className={styles.removeBtn}>✕</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.modalFooter}>
                    <button onClick={handleUpload} className={styles.uploadBtn}>
                        {isEditMode ? "Save Changes" : "Upload"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default IOModal;